import {
  ReactNode,
  ReactElement,
  cloneElement,
  isValidElement,
  useRef,
  Children,
} from "react";
import { useToast } from "@chakra-ui/react";
import { usePermissionsContext } from "../../data/permissions/permissionContext";

type ChildrenInteractionBlockerProps = {
  requiredPermissions: string[];
  children: ReactNode;
  toastMessage?: string;
};

// Wraps children and blocks all interactions when the user lacks every one of
// requiredPermissions. A single matching permission is enough to allow access
// (OR logic). Pass an empty array to always allow.
export const ChildrenInteractionBlocker = ({
  requiredPermissions,
  children,
  toastMessage = "Você não tem permissão para realizar esta ação.",
}: ChildrenInteractionBlockerProps) => {
  const { permissions } = usePermissionsContext();
  const toast = useToast();
  const toastActiveRef = useRef(false);

  const hasPermission =
    requiredPermissions.length === 0 ||
    requiredPermissions.some((rp) => permissions.includes(rp));

  if (hasPermission) {
    return <>{children}</>;
  }

  const handleBlockedInteraction = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!toastActiveRef.current) {
      toastActiveRef.current = true;
      toast({
        title: "Acesso negado",
        description: toastMessage,
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
        onCloseComplete: () => {
          toastActiveRef.current = false;
        },
      });
    }

    return false;
  };

  const wrapWithBlocker = (child: ReactNode): ReactNode => {
    if (!isValidElement(child)) return child;

    return cloneElement(child as ReactElement<any>, {
      onClick: handleBlockedInteraction,
      onMouseDown: handleBlockedInteraction,
      onTouchStart: handleBlockedInteraction,
      onKeyDown: handleBlockedInteraction,
      tabIndex: -1,
      disabled: true,
      style: {
        ...(child.props?.style || {}),
        pointerEvents: "auto",
        opacity: 0.5,
        cursor: "not-allowed",
      },
    });
  };

  return <>{Children.map(children, wrapWithBlocker)}</>;
};
